import { useMemo, useState } from 'react'
import { Plus, Edit, Trash2, FolderTree, ExternalLink, RotateCcw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCategories, useDeleteCategory, type ApiCategory } from '@/modules/categories-api'
import { useAdminProductsStore } from '../../../state/productsStore'
import { CategoryIconPreview } from '../../components/CategoryIconPreview'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { CategoryFormDialog } from './CategoryFormDialog'
import { useToast } from '../../components/Toast'

export function CategoriesListPage() {
  const { data: categories, isLoading } = useCategories()
  const deleteMut = useDeleteCategory()
  const products = useAdminProductsStore((s) => s.products)
  const push = useToast((s) => s.push)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ApiCategory | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const sorted = useMemo(() => categories.slice().sort((a, b) => a.order - b.order), [categories])
  const countByCat = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of products) m.set(p.categorySlug, (m.get(p.categorySlug) ?? 0) + 1)
    return m
  }, [products])

  const target = deleteId ? categories.find((c) => c.id === deleteId) : null
  const targetProductCount = target ? (target.productCount ?? countByCat.get(target.slug) ?? 0) : 0

  const handleDelete = async () => {
    if (!deleteId || !target) return
    try {
      const r = await deleteMut.mutateAsync(deleteId)
      if (r.ok) push('success', `Đã xóa "${target.label}"`)
      else push('error', r.message ?? 'Không xóa được')
    } catch (e: any) {
      push('error', e?.message ?? 'Lỗi mạng')
    }
    setDeleteId(null)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Danh mục</h1>
          <p className="text-sm text-muted mt-1">
            {isLoading ? 'Đang tải...' : `${categories.length} danh mục — chỉnh sửa áp dụng ngay cho site khách`}
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setFormOpen(true) }}
          className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Thêm danh mục
        </button>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium w-14">#</th>
                <th className="text-left px-4 py-3 font-medium">Danh mục</th>
                <th className="text-left px-4 py-3 font-medium">Slug</th>
                <th className="text-center px-4 py-3 font-medium">Sản phẩm</th>
                <th className="text-center px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-muted mb-3">
                      <FolderTree className="w-8 h-8" />
                    </div>
                    <div className="font-semibold text-text">Chưa có danh mục</div>
                    <div className="text-sm text-muted mt-1">Bấm "Thêm danh mục" để tạo mới</div>
                  </td>
                </tr>
              )}
              {sorted.map((c, i) => {
                const count = c.productCount ?? countByCat.get(c.slug) ?? 0
                return (
                  <tr key={c.id} className="border-t border-border hover:bg-slate-50">
                    <td className="px-4 py-3 text-muted font-mono text-xs">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                          <CategoryIconPreview name={c.icon} className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-medium text-text">{c.label}</div>
                          {c.description && <div className="text-xs text-muted line-clamp-1 max-w-[360px]">{c.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/danh-muc/${c.slug}`}
                        target="_blank"
                        className="font-mono text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
                      >
                        {c.slug} <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={[
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
                        count === 0 ? 'bg-slate-100 text-slate-600' : 'bg-brand-50 text-brand-700',
                      ].join(' ')}>
                        {count} SP
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-muted font-mono text-xs">{c.order}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setEditing(c); setFormOpen(true) }}
                          className="w-8 h-8 rounded-md hover:bg-brand-50 text-brand-600 flex items-center justify-center transition"
                          aria-label="Sửa"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(c.id)}
                          className="w-8 h-8 rounded-md hover:bg-red-50 text-danger flex items-center justify-center transition"
                          aria-label="Xóa"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CategoryFormDialog
        open={formOpen}
        editing={editing}
        onClose={() => setFormOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteId}
        title="Xóa danh mục"
        message={
          targetProductCount > 0
            ? `Danh mục "${target?.label}" đang có ${targetProductCount} sản phẩm. Backend sẽ chặn xóa. Vui lòng chuyển/xóa sản phẩm trước.`
            : `Bạn chắc chắn muốn xóa "${target?.label}"? Hành động không thể hoàn tác.`
        }
        confirmLabel="Xóa"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  )
}
